import { ThemeProvider } from '@mui/material/styles';
import { defaultTheme, fetchPortalFiles } from '../utilities/Utilites';
import * as React from 'react';
import Box from '@mui/system/Box';
import {
    Button,
    Card,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Link,
} from '@mui/material';
import { useGlobalStore } from '../utilities/GlobalState';
import {
    DistributorTypes,
    PortalFile,
    PriceList,
} from '../interfaces/Interfaces';
import { Datagrid } from '../utilities/Datagrid';
import { useEffect, useState } from 'react';
import { AuthGet } from '../utilities/Routing/Requests';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

export const QuotesPage = () => {
    const globalState = useGlobalStore((state) => state);
    const [quoteData, setQuoteData] = React.useState<Array<PortalFile>>();
    const [roleDialogOpen, setRoleDialogOpen] = useState<boolean>(false);
    const [customerRoleData, setCustomerRoleData] =
        useState<Array<DistributorTypes>>();
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

    const fetchDistributorTypes = (data: DistributorTypes) => {
        return AuthGet('/GetDistributorTypes', {
            'x-access-token': globalState?.authToken,
            id: data?.id,
            distributor_Type: data?.distributor_Type,
            description: data?.description,
        });
    };

    const fetchPriceList = (distributor_Type: string) => {
        return AuthGet('https://synthesis.aptmfg.com:7114/GetPriceList', {
            distributorType: distributor_Type,
        });
    };

    // 🗺️ Mapping of roles to distributor_Type and IDs
    useEffect(() => {
        const roleMapping: Record<
            string,
            { distributor_Type: string; id: number }
        > = {
            'EDU-MATERIAL HANDLING': {
                distributor_Type: 'Education - Material Handling',
                id: 9,
            },
            'EDU-ROCKWELL': {
                distributor_Type: 'Education - Rockwell',
                id: 10,
            },
            'EDU-WELDING': { distributor_Type: 'Education - Welding', id: 11 },
            PACKAGING: { distributor_Type: 'Packaging Equipment', id: 8 },
            WELDING: { distributor_Type: 'Welding Products', id: 12 },
        };

        const roleData = globalState?.customerRoles
            ?.split(',')
            ?.map((role) => ({
                id: roleMapping[role?.trim()?.toUpperCase()]?.id || -1,
                distributor_Type:
                    roleMapping[role?.trim()?.toUpperCase()]
                        ?.distributor_Type || 'Unknown',
                description: role?.trim(),
            }));

        setCustomerRoleData(roleData);
    }, [globalState?.customerRoles]);

    // 🐶 Fetching Price List
    const fetchList = async (distributor_Type: string) => {
        try {
            const listRes = await fetchPriceList(distributor_Type);

            const filtered = listRes?.data?.map((data: PriceList) => ({
                distributor_Type: distributor_Type,
                product_Name: data?.product_Name,
                material: data?.material,
                description: data?.description,
                ext_Description: data?.ext_Description,
                msrp: data?.msrp,
                sales_Lead_Time: data?.sales_Lead_Time,
                sales_Lead_Time_Unit: data?.sales_Lead_Time_Unit,
            }));

            return filtered;
        } catch (err) {
            toast?.error('Error fetching Price List.');
            return [];
        }
    };

    // 🐶 Fetching Distributor Type
    useEffect(() => {
        const fetchDistributorTypeData = async () => {
            if (customerRoleData) {
                const distributorData = await Promise.all(
                    customerRoleData?.map(async (role) => {
                        if (role?.id !== -1) {
                            try {
                                const res = await fetchDistributorTypes({
                                    id: role?.id,
                                    distributor_Type: role?.distributor_Type,
                                    description: '',
                                });
                                return res?.data;
                            } catch (error) {
                                toast?.error(
                                    `Error fetching data for role ID ${role?.id}: `,
                                );
                            }
                        }
                    }),
                );
                const validDistributorData = distributorData?.filter(
                    (data) => data !== null,
                );
                console.log('Fetched Distributor Data:', validDistributorData);
            }
        };

        fetchDistributorTypeData();
    }, [customerRoleData]);

    useEffect(() => {
        fetchPortalFiles(globalState.customerId, globalState?.authToken).then(
            (res) => {
                globalState.setPortalFiles(res.data);
                const cxRoles = globalState?.customerRoles
                    ?.slice(0, -1)
                    ?.split(',');
                const filteredData = res.data?.filter(
                    (item) =>
                        cxRoles?.includes(item?.type?.toUpperCase()) &&
                        item.category === 'QUOTATION',
                );
                setQuoteData(filteredData);
            },
        );
    }, []);

    const getRowId = (row: any) => {
        return row.document_id;
    };

    const handleCloseRoleDialog = () => {
        setRoleDialogOpen(false);
    };

    return (
        <ThemeProvider theme={defaultTheme}>
            <Box
                sx={{
                    padding: '30px 10px 10px 10px',
                    maxWidth: '100vp',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Card sx={{ padding: '10px', margin: '10px' }}>
                    <Grid
                        container
                        style={{
                            padding: '10px 10px 10px 20px',
                            backgroundColor: '#f44336',
                        }}
                    >
                        <Grid
                            item
                            sx={{
                                color: '#fff',
                                fontWeight: 'bold',
                                fontSize: '20px',
                            }}
                        >
                            <div>Pricing Information</div>
                        </Grid>
                        <Grid
                            sx={{
                                background: 'white',
                                borderRadius: '8px',
                                justifyContent: 'end',
                                marginLeft: ' auto',
                            }}
                        >
                            <Button
                                onClick={() => setRoleDialogOpen(true)}
                                sx={{
                                    fontWeight: 'bold',
                                }}
                            >
                                Download Price List
                            </Button>
                        </Grid>
                    </Grid>

                    {Datagrid({
                        getRowId: getRowId,
                        pageSize: 20,
                        columns: [
                            {
                                field: 'document_id',
                                headerName: 'Document',
                                type: 'string',
                                flex: 0.15,
                            },
                            {
                                field: 'file_name',
                                headerName: 'File Name',
                                type: 'string',
                                flex: 0.25,
                            },
                            {
                                field: 'product',
                                headerName: 'Product',
                                type: 'string',
                                flex: 0.25,
                            },
                            {
                                field: 'revision',
                                headerName: 'Revision',
                                type: 'string',
                                flex: 0.15,
                            },
                            {
                                field: 'download_url',
                                headerName: 'Link',
                                type: 'string',
                                flex: 0.5,
                                renderCell: (params) => {
                                    return (
                                        <div>
                                            <Link
                                                target="_blank"
                                                href={params?.row?.download_url}
                                                rel="noreferrer"
                                            >
                                                {params?.row?.download_url}
                                            </Link>
                                        </div>
                                    );
                                },
                            },
                        ],
                        data: quoteData || [],
                    })}
                </Card>
            </Box>

            <Box>
                <Dialog
                    open={roleDialogOpen}
                    onClose={handleCloseRoleDialog}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadiusBottom: '15px',
                    }}
                >
                    <Grid
                        container
                        style={{
                            margin: 'auto',
                            backgroundColor: '#f44336',
                        }}
                    >
                        <DialogTitle
                            sx={{
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: '#fff',
                            }}
                        >
                            Select Price Lists
                        </DialogTitle>
                    </Grid>
                    <DialogContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '10px',
                        }}
                    >
                        {customerRoleData
                            ?.filter(
                                (role) => role?.distributor_Type !== 'Unknown',
                            )
                            ?.map((role, index) => (
                                <FormControlLabel
                                    key={index}
                                    control={
                                        <Checkbox
                                            value={role?.distributor_Type || ''}
                                            onChange={(e) => {
                                                const { checked, value } =
                                                    e?.target;
                                                setSelectedRoles((prev) =>
                                                    checked
                                                        ? [...prev, value]
                                                        : prev?.filter(
                                                              (r) =>
                                                                  r !== value,
                                                          ),
                                                );
                                            }}
                                        />
                                    }
                                    label={role?.distributor_Type || 'Unknown'}
                                    sx={{ margin: '5px', width: '90%' }}
                                />
                            ))}
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ marginTop: '20px' }}
                            onClick={async () => {
                                if (selectedRoles?.length === 0) {
                                    toast?.error('Select at Least One Option.');
                                    return;
                                }

                                const priceListData: any[] = [];

                                for (const distributorType of selectedRoles) {
                                    const res = await fetchList(
                                        distributorType,
                                    );

                                    if (res && res?.length > 0) {
                                        priceListData?.push(
                                            ...res?.map((item: any) => ({
                                                ...item,
                                                distributor_Type:
                                                    distributorType,
                                            })),
                                        );
                                    } else {
                                        toast?.warn(
                                            `No Available Data for ${distributorType}. Contact your Sales Rep if the Issue Persists..`,
                                        );
                                    }
                                }

                                if (priceListData?.length > 0) {
                                    const worksheet =
                                        XLSX?.utils?.json_to_sheet(priceListData);
                                    const workbook = XLSX?.utils?.book_new();
                                    XLSX?.utils?.book_append_sheet(
                                        workbook,
                                        worksheet,
                                        'Price List',
                                    );
                                    XLSX?.writeFile(workbook, 'PriceList.xlsx');
                                    toast?.success('Price List Downloaded.');
                                } else {
                                    toast?.error(
                                        'No Available Data. Contact your Sales Rep if the Issue Persists.',
                                    );
                                }

                                setRoleDialogOpen(false);
                            }}
                        >
                            Download Price List
                        </Button>
                    </DialogContent>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
};
